/**
 * Google Analytics 追蹤工具
 */
import { event, pageview } from 'vue-gtag'

/**
 * GA 事件類別定義
 */
export enum GACategory {
  LOGIN = 'Login',
  ISP_INSTALL = 'ISP_Install',
  NAVIGATION = 'Navigation',
  HOME = 'Home',
  CONSULTATION = 'Consultation',
  ERROR = 'Error',
  WARNING = 'Warning',
  PERFORMANCE = 'Performance'
}

/**
 * GA 事件動作定義
 */
export enum GAAction {
  // 頁面瀏覽
  PAGE_VIEW = 'page_view',

  // 登入相關
  LOGIN_TYPE_SELECT = 'login_type_select',
  SMS_REQUEST = 'sms_request',
  LOGIN_SUBMIT = 'login_submit',
  LOGIN_SUCCESS = 'login_success',
  LOGIN_FAILED = 'login_failed',
  LINE_LOGIN_CLICK = 'line_login_click',
  LOGIN_CLICK = 'login_click',
  LOGOUT_CLICK = 'logout_click',
  LOGOUT_CONFIRM = 'logout_confirm',

  // Home 首頁
  FUNCTION_CLICK = 'function_click',

  // Consultation 一鍵諮詢
  CONNECTION_STATUS = 'connection_status',
  SERVICE_TYPE_SELECT = 'service_type_select',
  QUESTION_SELECT = 'question_select',
  APPOINTMENT_DATE_SELECT = 'appointment_date_select',
  APPOINTMENT_TIME_SELECT = 'appointment_time_select',
  APPOINTMENT_STEP_PROGRESS = 'appointment_step_progress',
  APPOINTMENT_SUBMIT = 'appointment_submit',
  APPOINTMENT_SUCCESS = 'appointment_success',
  APPOINTMENT_FAILED = 'appointment_failed',
  GUIDE_WEB_OPEN = 'guide_web_open',
  GUIDE_WEB_CLOSE = 'guide_web_close',
  ACCOUNT_SELECT_OPEN = 'account_select_open',
  ACCOUNT_SELECT_CANCEL = 'account_select_cancel',
  ACCOUNT_SELECTED = 'account_selected',

  // ISP 申裝流程
  COMMUNITY_SEARCH = 'community_search',
  COMMUNITY_SELECTED = 'community_selected',
  PLAN_TYPE_SWITCHED = 'plan_type_switched',
  PLAN_SELECTED = 'plan_selected',
  CLICK_LINE_CONSULT = 'click_line_consult',
  STEP_SELECT_COMMUNITY = 'step_select_community',
  SPECIAL_PLAN = 'special_plan',
  SPECIAL_PLAN_SELECTED = 'special_plan_selected',
  ADDON_ITEM_DETAIL = 'addon_item_detail',
  ADDON_ITEM_CHANGE = 'addon_item_change',
  ADDON_WARNING_MODAL_VIEW = 'addon_warning_modal_view',
  ADDON_WARNING_ACTION = 'addon_warning_action',
  NO_COMMUNITY_MODE = 'no_community_mode',
  STEP_CONTACT_INFO = 'step_contact_info',
  STEP_BACK = 'step_back',
  STEP_TIME_SPENT = 'step_time_spent',
  FLOW_ABANDONED = 'flow_abandoned',
  FORM_FILLED = 'form_filled',
  CONSENT_CHECKED = 'consent_checked',
  SUBMIT = 'submit',
  APPLY_COMPLETED = 'apply_completed',
  APPOINT_TIME_CLICK = 'appoint_time_click',
  ADDON_TOTAL_VALUE = 'addon_total_value',

  // 諮詢流程（consult-search → appoint-info → finish，「我要諮詢」入口）
  CITY_SELECTED = 'city_selected',
  DIST_SELECTED = 'dist_selected',
  ROAD_SELECTED = 'road_selected',
  COMMUNITY_ENTER = 'community_enter',
  COMMUNITY_BLOCK = 'community_block',
  STEP_APPOINT_INFO = 'step_appoint_info',
  SUBMIT_APPOINT = 'submit_appoint',
  APPOINT_COMPLETED = 'appoint_completed',

  // 預約相關
  DATE_SELECTED = 'date_selected',
  TIME_PERIOD_SELECTED = 'time_period_selected',
  APPOINT_SUBMIT = 'appoint_submit',
  APPOINT_SUCCESS = 'appoint_success',

  // 錯誤追蹤
  VALIDATION_ERROR = 'validation_error',
  API_ERROR = 'api_error',
  USER_WARNING = 'user_warning',

  // 效能追蹤
  API_RESPONSE_TIME = 'api_response_time',
  PAGE_LOAD_TIME = 'page_load_time'
}

/**
 * GA 追蹤事件參數介面
 */
interface GAEventParams {
  category: GACategory
  action: GAAction
  label?: string
  value?: number
  [key: string]: string | number | boolean | undefined
}

/**
 * 發送 GA 事件
 * @param params 事件參數
 */
export function trackEvent(params: GAEventParams): void {
  try {
    const { category, action, label, value, ...customParams } = params

    const eventData: Record<string, string | number | boolean | undefined> = {
      event_category: category,
      event_label: label,
      value,
      ...customParams
    }

    // 發送到 GA
    event(action, eventData)
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console -- 開發環境輸出GA追蹤錯誤以便調試
      console.error('GA tracking error:', error)
    }
  }
}

/**
 * 追蹤頁面瀏覽
 * @param pagePath 頁面路徑
 * @param pageTitle 頁面標題
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  try {
    // 使用 vue-gtag 的 pageview
    pageview({
      page_path: pagePath,
      page_title: pageTitle
    })
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console -- 開發環境輸出GA追蹤錯誤以便調試
      console.error('GA pageview error:', error)
    }
  }
}

/**
 * 登入相關追蹤
 */
export const GALogin = {
  pageView: () => { trackPageView('/ISP-App/login', '登入頁') },

  loginTypeSelect: (type: 'ISPUser' | 'NonISPUser') => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LOGIN_TYPE_SELECT,
      label: type
    })
  },

  smsRequest: (phone: string) => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.SMS_REQUEST,
      label: phone.substring(0, 4) + '****'
    })
  },

  loginSubmit: (method: 'phone' | 'line') => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LOGIN_SUBMIT,
      label: method
    })
  },

  loginSuccess: (method: 'phone' | 'line', custId?: string, additionalInfo?: {
    hasMultipleAccounts?: boolean
    accountCount?: number
    region?: string
  }) => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LOGIN_SUCCESS,
      label: method,
      user_id: custId,
      has_multiple_accounts: additionalInfo?.hasMultipleAccounts,
      account_count: additionalInfo?.accountCount,
      user_region: additionalInfo?.region
    })
  },

  loginFailed: (method: 'phone' | 'line', errorCode?: string) => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LOGIN_FAILED,
      label: method,
      error_code: errorCode
    })
  },

  lineLoginClick: () => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LINE_LOGIN_CLICK
    })
  },

  logoutClick: (isLoggedIn: boolean) => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LOGOUT_CLICK,
      label: isLoggedIn ? 'logged_in' : 'not_logged_in'
    })
  },

  logoutConfirm: (confirmed: boolean) => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LOGOUT_CONFIRM,
      label: confirmed ? 'yes' : 'no'
    })
  },

  multiAccountDetected: (accountCount: number) => {
    trackEvent({
      category: GACategory.LOGIN,
      action: GAAction.LOGIN_SUCCESS,
      label: 'multi_account',
      account_count: accountCount
    })
  }
}

/**
 * ISP 申裝流程追蹤（包含預約功能）
 */
export const GAInstall = {
  selectPlanPageView: () => { trackPageView('/ISP-install/step/select-plan', '步驟一：選擇方案') },

  searchNPickPageView: () => { trackPageView('/ISP-install/step/search-n-pick', '步驟二：查詢社區') },

  communitySearch: (city: string, keyword: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.COMMUNITY_SEARCH,
      label: `${city} - ${keyword}`
    })
  },

  communitySelected: (communityName: string, communityId: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.COMMUNITY_SELECTED,
      label: communityName,
      community_name: communityName,
      community_id: communityId
    })
  },

  // ===== 諮詢流程（consult-search → appoint-info → finish，「我要諮詢」入口）=====

  /** 諮詢步驟一：進入輸入社區頁 */
  consultSearchPageView: () => { trackPageView('/ISP-install/step/consult-search?consult=1', '諮詢：輸入社區') },

  /** 諮詢步驟一：點擊選擇縣市 */
  citySelected: (city: string) => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.CITY_SELECTED, label: city })
  },

  /** 諮詢步驟一：點擊選擇行政區 */
  distSelected: (dist: string) => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.DIST_SELECTED, label: dist })
  },

  /** 諮詢步驟一：點擊選擇路（段）名 */
  roadSelected: (road: string) => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.ROAD_SELECTED, label: road })
  },

  /** 諮詢步驟一：手動輸入社區名稱（查無對應路名社區時） */
  communityEnter: (communityName: string) => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.COMMUNITY_ENTER, label: communityName })
  },

  /** 諮詢步驟一：選擇社區後，該社區不開放申請 */
  communityBlock: (communityName: string) => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.COMMUNITY_BLOCK, label: communityName })
  },

  /** 諮詢步驟一：點擊下一步，進入填寫資訊頁（步驟二） */
  stepAppointInfo: (communityName?: string) => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.STEP_APPOINT_INFO, label: communityName })
  },

  /** 諮詢步驟二：進入填寫資訊頁 */
  apptInfoPageView: () => { trackPageView('/ISP-install/appoint-info', '諮詢：填寫資訊') },

  /** 諮詢步驟二：確認送出 */
  submitAppoint: () => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.SUBMIT_APPOINT })
  },

  /** 諮詢步驟三：進入完成申請頁 */
  apptFinishPageView: () => { trackPageView('/ISP-install/finish', '諮詢：完成申請') },

  /** 諮詢步驟三：完成申請（預約專人來電） */
  appointCompleted: () => {
    trackEvent({ category: GACategory.ISP_INSTALL, action: GAAction.APPOINT_COMPLETED })
  },

  /** 步驟一：切換方案類型（頁面上方 toggle） */
  planTypeSwitched: (planTypeName: string, planTypeId: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.PLAN_TYPE_SWITCHED,
      label: planTypeName,
      plan_type_name: planTypeName,
      plan_type_id: planTypeId
    })
  },

  /** 步驟一：選擇方案卡片 */
  planSelected: (planName: string, planId: string, amount?: number, isContactNeeded?: boolean) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.PLAN_SELECTED,
      label: planName,
      plan_name: planName,
      plan_id: planId,
      value: amount,
      is_contact_needed: isContactNeeded
    })
  },

  /** 點擊官方 LINE@ 連結（選擇方案頁／填寫資訊頁皆有此區塊）*/
  clickLineConsult: (stepName: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.CLICK_LINE_CONSULT,
      label: stepName,
      step_name: stepName
    })
  },

  /** 步驟一：點擊下一步，進入選擇社區頁（步驟二）*/
  stepSelectCommunity: (planName: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.STEP_SELECT_COMMUNITY,
      label: planName,
      plan_name: planName
    })
  },

  /** 步驟二：選完社區後顯示社區專屬方案（團購）區塊 */
  specialPlanShown: () => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.SPECIAL_PLAN,
      has_special_plan: true
    })
  },

  /** 步驟二：選擇社區專屬方案（團購） */
  specialPlanSelected: (planName: string, planId: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.SPECIAL_PLAN_SELECTED,
      label: planName,
      plan_name: planName,
      plan_id: planId
    })
  },

  /** 步驟二：查看加購商品介紹（彈窗開啟） */
  addonItemDetail: (itemName: string, itemId: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.ADDON_ITEM_DETAIL,
      label: itemName,
      item_name: itemName,
      item_id: itemId
    })
  },

  addonItemChange: (itemName: string, itemId: string, quantity: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.ADDON_ITEM_CHANGE,
      label: itemName,
      item_name: itemName,
      item_id: itemId,
      quantity
    })
  },

  /** 步驟二：未選加購時點下一步跳出的「申裝前確認」提醒彈窗 */
  addonWarningModalView: (planName: string, planId: string, addonCount: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.ADDON_WARNING_MODAL_VIEW,
      label: planName,
      plan_name: planName,
      plan_id: planId,
      addon_count: addonCount
    })
  },

  /** 步驟二：「申裝前確認」彈窗選擇返回加購或繼續訂購 */
  addonWarningAction: (action: 'back' | 'continue') => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.ADDON_WARNING_ACTION,
      label: action,
      action_type: action
    })
  },

  noCommunityMode: () => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.NO_COMMUNITY_MODE
    })
  },

  /** 步驟二：點擊下一步，進入填寫資訊頁（步驟三） */
  stepContactInfo: (planName?: string, planId?: string, addonCount?: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.STEP_CONTACT_INFO,
      label: planName,
      plan_name: planName,
      plan_id: planId,
      addon_count: addonCount
    })
  },

  infoFormPageView: () => { trackPageView('/ISP-install/step/info-form', '步驟三：填寫資訊') },

  consentChecked: (consentType: 'PII' | 'installService') => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.CONSENT_CHECKED,
      label: consentType
    })
  },

  /** 步驟三：確認申請（送出表單） */
  step2Submit: (planName?: string, planId?: string, addonCount?: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.SUBMIT,
      label: 'step2',
      plan_name: planName,
      plan_id: planId,
      addon_count: addonCount
    })
  },

  finishPageView: () => { trackPageView('/ISP-install/step/finish', '步驟四：完成申請') },

  applyCompleted: (applySn: string, totalAmount?: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.APPLY_COMPLETED,
      label: applySn,
      apply_sn: applySn,
      total_amount: totalAmount,
      value: totalAmount
    })
  },

  appointTimeClick: () => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.APPOINT_TIME_CLICK
    })
  },

  // 進階追蹤
  stepTimeSpent: (step: number, seconds: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.STEP_TIME_SPENT,
      label: `step_${step}`,
      value: seconds
    })
  },

  flowAbandoned: (currentStep: number, reason?: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.FLOW_ABANDONED,
      label: `step_${currentStep}`,
      abandon_reason: reason
    })
  },

  stepBack: (fromStep: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.STEP_BACK,
      label: `from_step_${fromStep}`
    })
  },

  addonTotalValue: (totalAmount: number, itemCount: number) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.ADDON_TOTAL_VALUE,
      value: totalAmount,
      item_count: itemCount
    })
  }
}

/**
 * 預約流程追蹤（屬於申裝流程的一部分）
 */
export const GAAppoint = {
  pageView: () => { trackPageView('/ISP-install/appoint', '預約裝機時間') },

  dateSelected: (date: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.DATE_SELECTED,
      label: date
    })
  },

  timePeriodSelected: (timeRange: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.TIME_PERIOD_SELECTED,
      label: timeRange
    })
  },

  appointSubmit: (date: string, timeRange: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.APPOINT_SUBMIT,
      label: `${date} ${timeRange}`
    })
  },

  appointSuccess: (date: string, timeRange: string) => {
    trackEvent({
      category: GACategory.ISP_INSTALL,
      action: GAAction.APPOINT_SUCCESS,
      label: `${date} ${timeRange}`
    })
  }
}

/**
 * Home 首頁追蹤
 */
export const GAHome = {
  pageView: () => { trackPageView('/ISP-App/home', '首頁') },

  functionClick: (functionName: string, isOpen: boolean, needAuth: boolean) => {
    trackEvent({
      category: GACategory.HOME,
      action: GAAction.FUNCTION_CLICK,
      label: functionName,
      is_available: isOpen,
      requires_auth: needAuth
    })
  },

  loginClick: (isLoggedIn: boolean) => {
    trackEvent({
      category: GACategory.NAVIGATION,
      action: isLoggedIn ? GAAction.LOGOUT_CLICK : GAAction.LOGIN_CLICK,
      label: isLoggedIn ? 'from_home' : 'from_home'
    })
  }
}

/**
 * 一鍵諮詢流程追蹤
 */
export const GAConsultation = {
  pageView: () => { trackPageView('/ISP-App/consultation', '一鍵諮詢') },

  connectionStatus: (engineOnline: boolean, clientOnline: boolean, hasError: boolean) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.CONNECTION_STATUS,
      engine_online: engineOnline,
      client_online: clientOnline,
      has_error: hasError
    })
  },

  serviceTypeClick: (serviceType: 'network' | 'account') => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.SERVICE_TYPE_SELECT,
      label: serviceType
    })
  },

  questionSelect: (serviceType: string, questionLabel: string) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.QUESTION_SELECT,
      label: questionLabel,
      service_type: serviceType
    })
  },

  appointmentDateSelect: (date: string, isHoliday: boolean) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.APPOINTMENT_DATE_SELECT,
      label: date,
      is_holiday: isHoliday
    })
  },

  appointmentTimeSelect: (timeSlot: string, totalSelected: number) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.APPOINTMENT_TIME_SELECT,
      label: timeSlot,
      total_selected: totalSelected
    })
  },

  appointmentStepProgress: (step: number) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.APPOINTMENT_STEP_PROGRESS,
      label: `step_${step}`
    })
  },

  appointmentSubmit: (questionLabel: string, date: string, times: string[]) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.APPOINTMENT_SUBMIT,
      label: questionLabel,
      appointment_date: date,
      appointment_times: times.join(',')
    })
  },

  appointmentSuccess: () => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.APPOINTMENT_SUCCESS
    })
  },

  appointmentFailed: (errorMsg: string) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.APPOINTMENT_FAILED,
      label: errorMsg
    })
  },

  guideWebOpen: (url: string) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.GUIDE_WEB_OPEN,
      label: url
    })
  },

  guideWebClose: (url: string, timeSpent?: number) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.GUIDE_WEB_CLOSE,
      label: url,
      value: timeSpent
    })
  },

  accountSelectOpen: () => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.ACCOUNT_SELECT_OPEN
    })
  },

  accountSelectCancel: () => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.ACCOUNT_SELECT_CANCEL
    })
  },

  accountSelected: (accountIndex: number, totalAccounts: number) => {
    trackEvent({
      category: GACategory.CONSULTATION,
      action: GAAction.ACCOUNT_SELECTED,
      label: `${accountIndex + 1}/${totalAccounts}`
    })
  }
}

/**
 * 錯誤與警告追蹤
 */
export const GAError = {
  validation: (page: string, fieldName: string, errorType: string) => {
    trackEvent({
      category: GACategory.ERROR,
      action: GAAction.VALIDATION_ERROR,
      label: `${page} - ${fieldName}`,
      error_type: errorType
    })
  },

  apiError: (apiName: string, statusCode: number, errorMsg: string) => {
    trackEvent({
      category: GACategory.ERROR,
      action: GAAction.API_ERROR,
      label: apiName,
      status_code: statusCode,
      error_message: errorMsg
    })
  },

  userWarning: (page: string, warningMsg: string) => {
    trackEvent({
      category: GACategory.WARNING,
      action: GAAction.USER_WARNING,
      label: `${page} - ${warningMsg}`
    })
  }
}

/**
 * 效能追蹤
 */
export const GAPerformance = {
  apiResponseTime: (apiName: string, milliseconds: number) => {
    trackEvent({
      category: GACategory.PERFORMANCE,
      action: GAAction.API_RESPONSE_TIME,
      label: apiName,
      value: milliseconds
    })
  },

  pageLoadTime: (pageName: string, milliseconds: number) => {
    trackEvent({
      category: GACategory.PERFORMANCE,
      action: GAAction.PAGE_LOAD_TIME,
      label: pageName,
      value: milliseconds
    })
  }
}
